import { useRef } from 'react';
import type { RoomFile } from '@shared/protocol';
import type { Upload } from '../hooks/useUploads';
import { fileKind, fileRef } from '../lib/files';
import { useConfirm } from './ConfirmDialog';
import { DownloadIcon, FileIcon, OpenIcon, TrashIcon, UploadIcon } from './icons';

interface Props {
  roomId: string;
  roomName: string;
  files: RoomFile[];
  uploads: Upload[];
  onUpload: (files: File[]) => void;
  onDismissUpload: (id: string) => void;
  /** Puts a reference to the file into the message being written. */
  onReference: (file: RoomFile) => void;
  /** Paths the message being written already references. */
  referenced: Set<string>;
  onError: (message: string) => void;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * Images show themselves; everything else gets an icon for its kind. The
 * thumbnail is the full file scaled down -- fine for screenshots, and lazy
 * so a long list does not fetch what is scrolled out of view.
 */
function Preview({ file, url }: { file: RoomFile; url: string }) {
  const kind = fileKind(file.name);
  if (kind === 'image') {
    return (
      <span className="file__preview file__preview--image">
        <img src={url} alt="" loading="lazy" decoding="async" />
      </span>
    );
  }
  return (
    <span className={`file__preview file__preview--${kind}`}>
      {/* A word, not a glyph: "PDF" drawn inside a 20px icon is unreadable. */}
      {kind === 'pdf' ? <span className="file__badge">PDF</span> : <FileIcon size={18} />}
    </span>
  );
}

export function ago(at: number): string {
  const s = Math.round((Date.now() - at) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(at).toLocaleDateString();
}

export function FilesPanel({
  roomId,
  roomName,
  files,
  uploads,
  onUpload,
  onDismissUpload,
  onReference,
  referenced,
  onError,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();

  const base = `/api/rooms/${encodeURIComponent(roomId)}/files`;

  async function remove(file: RoomFile) {
    const ok = await confirm({
      tone: 'danger',
      title: `Delete “${file.name}”?`,
      body: 'It is removed from this room for everyone.',
      confirmLabel: 'Delete file',
    });
    if (!ok) return;
    const r = await fetch(`${base}/${encodeURIComponent(file.name)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    }).catch(() => undefined);
    if (!r?.ok) onError(`Could not delete ${file.name}.`);
  }

  return (
    <>
      <p className="files__sub">
        Shared with everyone in “{roomName}”. Click a file to reference it in your message.
      </p>

      <button className="files__drop" onClick={() => inputRef.current?.click()}>
        <UploadIcon size={18} />
        <span>
          <strong>Choose files</strong>
          <span>or drop them anywhere on the page</span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const picked = [...(e.target.files ?? [])];
          e.target.value = ''; // picking the same file again should upload again
          if (picked.length) onUpload(picked);
        }}
      />

      <div className="files__list">
        {uploads.map((u) => (
          <div className={`file file--pending ${u.error ? 'file--failed' : ''}`} key={u.id}>
            <span className="file__text">
              <span className="file__name">{u.name}</span>
              <span className="file__meta">
                {u.error ?? `Uploading · ${formatSize(u.loaded)} of ${formatSize(u.total)}`}
              </span>
              {!u.error && (
                <span className="file__bar">
                  <span style={{ transform: `scaleX(${u.total ? u.loaded / u.total : 0})` }} />
                </span>
              )}
            </span>
            {u.error && (
              <button
                className="file__action"
                onClick={() => onDismissUpload(u.id)}
                aria-label={`Dismiss ${u.name}`}
                title="Dismiss"
              >
                ×
              </button>
            )}
          </div>
        ))}

        {files.length === 0 && uploads.length === 0 && (
          <p className="files__empty">Nothing here yet.</p>
        )}

        {files.map((f) => {
          const inMessage = referenced.has(f.path);
          return (
          <div className={`file ${inMessage ? 'file--referenced' : ''}`} key={f.name}>
            <button
              className="file__main"
              onClick={() => onReference(f)}
              aria-pressed={inMessage}
              title={
                inMessage
                  ? `Already in your message as ${fileRef(f.path)}`
                  : `Reference in message: ${fileRef(f.path)}`
              }
            >
              <Preview file={f} url={`${base}/${encodeURIComponent(f.name)}?inline`} />
              <span className="file__text">
                {/* The name truncates; the tag never does. */}
                <span className="file__name-row">
                  <span className="file__name">{f.name}</span>
                  {inMessage && <span className="file__tag">In message</span>}
                </span>
                <span className="file__meta">
                  {formatSize(f.size)} · {f.uploadedBy ? `${f.uploadedBy.name} · ` : ''}
                  {ago(f.modifiedAt)}
                </span>
              </span>
            </button>
            <a
              className="file__action"
              href={`${base}/${encodeURIComponent(f.name)}?inline`}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${f.name}`}
              title="Open in a new tab"
            >
              <OpenIcon />
            </a>
            <a
              className="file__action"
              href={`${base}/${encodeURIComponent(f.name)}`}
              download
              aria-label={`Download ${f.name}`}
              title="Download"
            >
              <DownloadIcon />
            </a>
            <button
              className="file__action file__action--danger"
              onClick={() => void remove(f)}
              aria-label={`Delete ${f.name}`}
              title="Delete"
            >
              <TrashIcon />
            </button>
          </div>
          );
        })}
      </div>
    </>
  );
}
